export type ResourceNodeType =
  | 'herb_bush'
  | 'iron_vein'
  | 'crystal_geode'
  | 'slime_residue_pool';

export interface ResourceNodeOptions {
  id: string;
  name: string;
  type: ResourceNodeType;
  maxHealth: number;
  yieldItem: { itemId: string; name: string; quantity: number };
  respawnTimeSec?: number;
}

export interface DamageResult {
  hit: boolean;
  destroyed: boolean;
  damageDealt: number;
  yield?: { itemId: string; name: string; quantity: number };
}

export class ResourceNode {
  public readonly id: string;
  public readonly name: string;
  public readonly type: ResourceNodeType;
  public readonly maxHealth: number;
  public readonly yieldItem: { itemId: string; name: string; quantity: number };
  public readonly respawnTimeSec: number;

  public currentHealth: number;
  public isDepleted: boolean = false;
  private respawnTimer: number = 0;

  constructor(options: ResourceNodeOptions) {
    this.id = options.id;
    this.name = options.name;
    this.type = options.type;
    this.maxHealth = options.maxHealth;
    this.currentHealth = options.maxHealth;
    this.yieldItem = options.yieldItem;
    this.respawnTimeSec = options.respawnTimeSec ?? 15;
  }

  public damage(amount: number): DamageResult {
    if (this.isDepleted) {
      return { hit: false, destroyed: false, damageDealt: 0 };
    }

    const applied = Math.min(this.currentHealth, amount);
    this.currentHealth -= applied;

    if (this.currentHealth <= 0) {
      this.isDepleted = true;
      this.respawnTimer = 0;
      return {
        hit: true,
        destroyed: true,
        damageDealt: applied,
        yield: { ...this.yieldItem }
      };
    }

    return {
      hit: true,
      destroyed: false,
      damageDealt: applied
    };
  }

  public update(dt: number): boolean {
    if (!this.isDepleted) return false;

    this.respawnTimer += dt;
    if (this.respawnTimer >= this.respawnTimeSec) {
      this.isDepleted = false;
      this.currentHealth = this.maxHealth;
      this.respawnTimer = 0;
      return true; // Node respawned
    }

    return false;
  }
}
